/**
 * AWS CUR (Cost & Usage Reports) configuration block.
 *
 * Lives inside the AWS account Edit dialog under CloudConfig — used to be a
 * standalone page under FinOps. The user decides at AWS-account configure time
 * whether to enable CUR (free Athena reads) or stay on Cost Explorer ($).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../../../lib/api';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Badge } from '../../../components/ui/badge';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import {
  FileText, CheckCircle2, AlertTriangle, Loader2, Copy, ChevronDown,
  ChevronUp, Sparkles, ExternalLink, Download,
} from 'lucide-react';
import { toast } from 'sonner';

const REQUIRED_FIELDS = [
  {
    key: 'cur_bucket',
    label: 'CUR S3 bucket name',
    placeholder: 'cloudgenie-cur-123456789012-us-east-1',
    hint: 'Bucket NAME only (no s3:// and no arn:…). Find at AWS Console → S3 → bucket name. CFN output: CURBucketName.',
  },
  {
    key: 'cur_prefix',
    label: 'CUR S3 prefix (path inside the bucket)',
    placeholder: 'cloudgenie-cur',
    hint: 'Prefix you chose when creating the CUR report (no leading or trailing slash). CFN output: CURPrefix.',
  },
  {
    key: 'athena_database',
    label: 'Athena database name',
    placeholder: 'cloudgenie_cur_db',
    hint: 'Glue/Athena DATABASE name (lowercase, underscores). AWS Console → Athena → Editor → Database dropdown. CFN output: AthenaDatabase.',
  },
  {
    key: 'athena_table',
    label: 'Athena table name',
    placeholder: 'cloudgenie_cur_cloudgenie_cur_report',
    hint: 'Glue/Athena TABLE name inside the database above (AWS prefixes with cur_… by convention). CFN output: AthenaTable.',
  },
  {
    key: 'athena_workgroup',
    label: 'Athena workgroup',
    placeholder: 'primary',
    hint: 'Workgroup NAME (default is "primary"). AWS Console → Athena → Workgroups. CFN output: AthenaWorkgroup.',
  },
  {
    key: 'athena_results_bucket',
    label: 'Athena query-results S3 bucket name',
    placeholder: 'cloudgenie-cur-athena-results-123456789012',
    hint: 'Separate bucket where Athena writes query CSV results — NOT the CUR bucket. Bucket NAME only. CFN output: AthenaResultsBucket.',
  },
  {
    key: 'region',
    label: 'CUR + Athena AWS region',
    placeholder: 'us-east-1',
    hint: 'Region CODE where the CUR + Athena resources live (e.g. us-east-1, eu-west-1). Must match the CFN stack region.',
  },
];

const TEMPLATE_URL = '/cloudgenie-cur-setup.yaml';
const buildLaunchUrl = (region = 'us-east-1') => {
  const tplUrl = `${window.location.origin}${TEMPLATE_URL}`;
  return `https://console.aws.amazon.com/cloudformation/home?region=${region}#/stacks/create/review?templateURL=${encodeURIComponent(tplUrl)}&stackName=cloudgenie-cur-setup`;
};

const emptyConfig = () => ({
  enabled: false,
  cur_bucket: '',
  cur_prefix: 'cloudgenie-cur',
  athena_database: '',
  athena_table: '',
  athena_workgroup: 'primary',
  athena_results_bucket: '',
  region: 'us-east-1',
});

export default function AwsCurSection({
  accountId,
  initialConfig,
  defaultRegion,
  // Deferred mode (Add Account flow — no accountId yet):
  // parent controls the draft and persists it after account creation.
  deferred = false,
  value,
  onChange,
}) {
  const [expanded, setExpanded] = useState(false);
  const [internalCfg, setInternalCfg] = useState(() => ({
    ...emptyConfig(),
    ...(initialConfig || {}),
  }));
  const cfg = deferred ? (value || emptyConfig()) : internalCfg;
  const setCfg = deferred
    ? (next) => onChange?.(typeof next === 'function' ? next(cfg) : next)
    : setInternalCfg;
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    if (deferred) return;
    setInternalCfg({
      ...emptyConfig(),
      region: defaultRegion || 'us-east-1',
      ...(initialConfig || {}),
    });
    setTestResult(null);
  }, [accountId, initialConfig, defaultRegion, deferred]);

  const onField = (k, v) => setCfg((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!accountId) {
      toast.error('Save the AWS account first, then re-open Edit to configure CUR.');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/cloud/accounts/${accountId}/cur`, cfg, { withCredentials: true });
      toast.success('CUR configuration saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save CUR configuration');
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    if (!accountId) {
      toast.error('Save the AWS account first.');
      return;
    }
    setTesting(true);
    try {
      await axios.put(`${API}/cloud/accounts/${accountId}/cur`, cfg, { withCredentials: true });
      const r = await axios.post(`${API}/cloud/accounts/${accountId}/cur/test`, {}, { withCredentials: true });
      setTestResult(r.data);
      if (r.data?.ok) toast.success(r.data.message || 'Athena reachable ✓');
      else toast.error(r.data?.message || 'CUR test failed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test request failed');
    } finally {
      setTesting(false);
    }
  };

  const copyIamSnippet = () => {
    navigator.clipboard.writeText(
      'athena:StartQueryExecution, athena:GetQueryExecution, athena:GetQueryResults, glue:GetTable, glue:GetPartitions, s3:GetObject, s3:ListBucket, s3:PutObject'
    ).then(() => toast.success('Copied'), () => toast.error('Copy failed'));
  };

  const launchUrl = buildLaunchUrl(cfg.region || defaultRegion || 'us-east-1');

  return (
    <div className="border border-border rounded-lg overflow-hidden" data-testid="aws-cur-section">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full px-4 py-3 flex items-center justify-between gap-3 hover:bg-secondary/40 transition-colors text-left"
        data-testid="aws-cur-toggle"
      >
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-amber-400" />
          <div>
            <div className="text-sm font-medium">AWS Cost Reports (CUR)</div>
            <div className="text-[11px] text-muted-foreground">
              Use your own CUR + Athena for billing data (free). Optional — defaults to Cost Explorer.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {cfg.enabled ? (
            <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 bg-emerald-500/5">
              <CheckCircle2 className="h-3 w-3 mr-1" /> CUR
            </Badge>
          ) : (
            <Badge variant="outline" className="border-muted-foreground/40 text-muted-foreground">
              Cost Explorer
            </Badge>
          )}
          {expanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
        </div>
      </button>

      {expanded && (
        <div className="p-4 border-t border-border space-y-4 bg-secondary/20" data-testid="aws-cur-form">
          {!accountId && !deferred && (
            <Alert className="border-amber-500/40 bg-amber-500/5">
              <AlertTriangle className="h-4 w-4 text-amber-400" />
              <AlertDescription className="text-xs">
                Save the account first — CUR config attaches to a saved AWS account.
              </AlertDescription>
            </Alert>
          )}
          {deferred && (
            <Alert className="border-blue-500/40 bg-blue-500/5">
              <CheckCircle2 className="h-4 w-4 text-blue-400" />
              <AlertDescription className="text-xs">
                These values are saved together with the account when you click <strong>Connect Account</strong>.
                You can test the connection right after.
              </AlertDescription>
            </Alert>
          )}

          <div className="flex items-center justify-between">
            <Label className="text-sm">Enable CUR for this account</Label>
            <Switch
              checked={!!cfg.enabled}
              onCheckedChange={(v) => onField('enabled', !!v)}
              data-testid="aws-cur-enabled"
            />
          </div>

          {/* CloudFormation launcher — easiest path */}
          <div className="rounded-md bg-amber-500/5 border border-amber-500/30 p-3">
            <div className="text-[11px] text-muted-foreground mb-2">
              Don&apos;t have CUR set up yet? Launch the bundled CloudFormation stack — it provisions
              the CUR report, S3 buckets, Athena DB/table, and a read-only IAM policy in ~2 minutes.
            </div>
            <div className="flex flex-wrap gap-2">
              <a href={launchUrl} target="_blank" rel="noreferrer">
                <Button size="sm" variant="outline" data-testid="aws-cur-launch-cfn">
                  <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                  Launch in AWS Console
                </Button>
              </a>
              <a href={TEMPLATE_URL} download>
                <Button size="sm" variant="ghost" data-testid="aws-cur-download-tpl">
                  <Download className="h-3.5 w-3.5 mr-1.5" />
                  Download template
                </Button>
              </a>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {REQUIRED_FIELDS.map(({ key, label, placeholder, hint }) => (
              <div key={key}>
                <Label className="text-xs text-muted-foreground">{label}</Label>
                <Input
                  value={cfg[key] || ''}
                  placeholder={placeholder}
                  onChange={(e) => onField(key, e.target.value)}
                  disabled={!cfg.enabled}
                  className="mt-1 font-mono text-xs"
                  data-testid={`aws-cur-${key}`}
                />
                <p className="text-[10px] text-muted-foreground/70 mt-1 leading-snug">{hint}</p>
              </div>
            ))}
          </div>

          {testResult && (
            <Alert className={testResult.ok
                ? 'border-emerald-500/40 bg-emerald-500/5'
                : 'border-rose-500/40 bg-rose-500/5'}
              data-testid="aws-cur-test-result">
              {testResult.ok
                ? <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                : <AlertTriangle className="h-4 w-4 text-rose-400" />}
              <AlertDescription className="text-xs">
                <strong>{testResult.ok ? 'Connection OK.' : `Failed (${testResult.reason}).`}</strong>{' '}
                {testResult.message}
                {testResult.ok && testResult.row_count != null && (
                  <span className="ml-2 text-muted-foreground">
                    ({testResult.row_count.toLocaleString()} rows in {testResult.database}.{testResult.table})
                  </span>
                )}
              </AlertDescription>
            </Alert>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-[11px] text-muted-foreground">
              IAM policy needs Athena + Glue read + S3 read on CUR bucket.{' '}
              <button
                type="button"
                onClick={copyIamSnippet}
                className="text-amber-400 hover:underline inline-flex items-center gap-1"
                data-testid="aws-cur-copy-iam"
              >
                <Copy className="h-3 w-3" /> copy
              </button>
            </div>
            <div className="flex gap-2">
              {!deferred && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={runTest}
                    disabled={!accountId || !cfg.enabled || testing}
                    data-testid="aws-cur-test-btn"
                  >
                    {testing
                      ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
                    Test
                  </Button>
                  <Button
                    size="sm"
                    onClick={save}
                    disabled={!accountId || saving}
                    data-testid="aws-cur-save-btn"
                    className="bg-amber-500 hover:bg-amber-600 text-black"
                  >
                    {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
                    Save CUR
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
